import { PropTypes, React } from 'imports';
import ReactDOM from 'react-dom';
import { addEvent, isAncestorOrSelf, isOutEvent, isOverEvent, removeEvent } from 'utils';
import Enum from 'utils/Enum';
import common from '!!less-vars-loader!static/common.less';
import vars from '!!less-vars-loader!./Popup.less';
import './Popup.less';

export const EDGE_WIDTH = parseInt(vars.edgeWidth);
export const ARROW_SIZE = parseInt(vars.arrowSize);
export const BORDER_WIDTH = parseInt(common.borderWidthDefault);

export const CloseMode = Enum('INSIDE', 'OUTSIDE', 'CLOSE_BUTTON');

const ESC_KEY_CODE = 27;

class PassContext extends React.Component {
    static get propTypes() {
        return {
            intl: PropTypes.object,
            router: PropTypes.object,
            store: PropTypes.object,
            fixed: PropTypes.bool,
            parent: PropTypes.object
        };
    }

    static get childContextTypes() {
        return {
            intl: PropTypes.object,
            router: PropTypes.object,
            store: PropTypes.object,
            fixed: PropTypes.bool,
            parent: PropTypes.object
        };
    }

    getChildContext() {
        return { intl: this.props.intl, router: this.props.router, store: this.props.store, fixed: this.props.fixed, parent: this.props.parent };
    }

    render() {
        return this.props.children;
    }
}

// TODO avoid handling native events
// there is no such event as "click outside" to close Popup
// one way is to listen to document click, check if target is not one of descendants and consider this event as click outside
// but React has its own event system which also listen events from document and creates fake events for each components after
// so this approach breaks order of events and React way to control propagation
// for example it is pretty tricky to implement DropPanel which could be closed by its title click -
//   first DropPanel handles Popup.onClose event raised by document and closes itself, then it handles its own onClick event from React and being already closed it opens again
// or to nest Popups -
//   when user clicks content of nested Popup parent receives document.onclick
//   and since all Popups do not nest DOM under their parents but right under body (to ensure position being relative to body)
//   parent handles this as outside click and closes istelf
//   calling stopPropagation on child content click will do nothing since document event will be handled before React propagation starts
//
// best solution would be to implement component which wraps all application
// and its counterpart which can be nested and proxies events when they propagate up to ascendant using context
// this way any component could use composition and atatch to outside events in React way
// preserving order and ability to control propagation from any component in chain as it should be
//
// but for now there are two hacks
// 1. handle document event after timeout to ensure React event propagation completed
// 2. extend native event with some custom field (isPostReactPropagationStopped) every time any component calls stopPropagation and check it in document click handler
//      assuming document is always higher in propagation chain than any React controlled node

const stopPropagation = Event.prototype.stopPropagation;
Event.prototype.stopPropagation = function () {
    stopPropagation.apply(this, arguments);
    this.isPostReactPropagationStopped = true;
};

const documentListeningComponents = [];
const listenTo = (eventName, methodName) => {
    let onDocumentEventTimeoutID = null;
    addEvent(document, eventName, function (event) {
        if (onDocumentEventTimeoutID) {
            clearTimeout(onDocumentEventTimeoutID);
            onDocumentEventTimeoutID = null;
        }
        // call them after timeout to ensure all React's document listeners have finished
        setTimeout(() => { documentListeningComponents.copy().forEach(component => component[methodName](event)); });
    });
};

listenTo('mousedown', '_onDocumentMouseDown');
listenTo('click', '_onDocumentClick');
listenTo('keydown', '_onDocumentKeyDown');

export const closeModePropType = PropTypes.oneOfType([
    PropTypes.oneOf(Object.values(CloseMode)),
    PropTypes.arrayOf(PropTypes.oneOf(Object.values(CloseMode)))
]);

export const ArrowSide = Enum('TOP', 'RIGHT', 'LEFT', 'BOTTOM');
export const DimIntencity = Enum('DEFAULT', 'LIGHTER');

export default class Popup extends React.PureComponent {
    static get ARROW_SIZE() {
        return ARROW_SIZE;
    }

    static get BORDER_WIDTH() {
        return BORDER_WIDTH;
    }

    static get EDGE_WIDTH() {
        return EDGE_WIDTH;
    }

    static get CloseMode() {
        return CloseMode;
    }

    static get ArrowSide() {
        return ArrowSide;
    }

    static get DimIntencity() {
        return DimIntencity;
    }

    static get closeModePropType() {
        return closeModePropType;
    }

    static get propTypes() {
        return {
            closeMode: closeModePropType,
            dimmed: PropTypes.bool,
            dimIntencity: PropTypes.oneOf(Object.values(DimIntencity)),
            left: PropTypes.number,
            top: PropTypes.number,
            right: PropTypes.number,
            bottom: PropTypes.number,
            fixed: PropTypes.bool,
            arrowSide: PropTypes.oneOf(Object.values(ArrowSide)),
            arrowOffset: PropTypes.number,
            onClose: PropTypes.func,
            className: PropTypes.string,
            onMouseOver: PropTypes.func,
            onMouseOut: PropTypes.func,
            onResize: PropTypes.func,
            children: PropTypes.oneOfType([PropTypes.node, PropTypes.func])
        };
    }

    static get defaultProps() {
        return {
            dimmed: false,
            dimIntencity: DimIntencity.DEFAULT,
            fixed: true,
            closeMode: CloseMode.OUTSIDE
        };
    }

    static get contextTypes() {
        return {
            intl: PropTypes.object,
            router: PropTypes.object,
            store: PropTypes.object,
            fixed: PropTypes.bool,
            parent: PropTypes.object
        };
    }

    _mountingTimeoutID = null;
    _dependenciesTimeoutID = null;
    _mutationObserver = null;
    _mouseStartPosition = { x: 0, y: 0 };

    componentDidMount() {
        document.body.appendChild(this.container = document.createElement('div'));
        this.container.className = 'popup-container' + (this.context.fixed || this.props.fixed ? ' fixed' : '') +
            (this.props.dimmed ? ' dimmed' : '') + (this.props.dimIntencity === DimIntencity.LIGHTER ? ' lighter-dim' : '');

        this.componentDidUpdate(this.props);

        this._mutationObserver = new MutationObserver(this._onDOMChange);
        this._mutationObserver.observe(this.container, { attributes: true, childList: true, characterData: true, subtree: true });
        this._onDOMChange();

        addEvent(document.body, 'DOMMouseScroll', this._onDocumentScroll);
        addEvent(document.body, 'mousewheel', this._onDocumentScroll);
        addEvent(document.body, 'touchmove', this._onDocumentScroll);
        addEvent(document.body, 'keydown', this._onOwnDocumentKeyDown);
        addEvent(this.container.firstElementChild, 'DOMMouseScroll', this._onScroll);
        addEvent(this.container.firstElementChild, 'mousewheel', this._onScroll);
        addEvent(this.container.firstElementChild, 'touchmove', this._onScroll);

        // Popup might be created because of click on some element
        // updates inside event handlers are performed synchronously, 
        // so by the end of the handler and bubbling up to the document 
        // the Popup will already be listening to document click, will misinterpret it as an outside one and try to close itself immediately
        // so let's add document listeners after timeout (bigger than document listeners' own one) to avoid such case
        this._mountingTimeoutID = setTimeout(() => documentListeningComponents.unshift(this), 1);

        // content of a Popup might cause some images or fonts to be loaded 
        // and it's nearly impossible to detect whether such requests have been spawned and when they are finished
        // but if and when they are finished the content size might change
        // so let's just simulate layout change after some time and hope that all fonts and images will have been loaded by that time
        this._dependenciesTimeoutID = setTimeout(this._onDOMChange, 1000);
    }

    componentDidUpdate(prevProps) {
        // popup treats coordinates relative to the page when it's not fixed (thus scrolls with the page)
        // and relative to visible part when it's fixed (since scrolling doesn't change it's visible position and position relative to the page changes while scrolling)
        // but all popups nested into fixed popups should be fixed as well, otherwise they will go with the page scrolling and their anchor will preserve visible position on the screen
        // component's who provide coordinates to such nested popups can not and shouln't know about their parent popup props
        // so let's convert page coordinates into window coordinates when we know the popup doesn't want to but forced to be fixed
        // TODO if fixed popup has it's own scrollbar, then nested non-fixed popups should follow that scrollbar, but how?
        const scrollingOffset = !this.props.fixed && this.context.fixed ? (document.scrollingElement || document.documentElement).scrollTop : 0;
        const l = typeof this.props.left === 'number';
        const t = typeof this.props.top === 'number';
        const r = typeof this.props.right === 'number';
        const b = typeof this.props.bottom === 'number';

        const style = {
            left: l ? `${Math.round(this.props.left)}px` : r ? `${Math.round(this.props.right)}px` : '50%',
            // this should expand when content is wider, i. e. in selects - popup should fit it's parent width if items are smaller, but show them fully when they are wider
            minWidth: l && r ? `${Math.round(this.props.right - this.props.left)}px` : 'auto',
            top: t ? `${Math.round(this.props.top - scrollingOffset)}px` : b ? `${Math.round(this.props.bottom - scrollingOffset)}px` : '50%',
            // this should be limited, i. e. when select has more items than screen can show, in this case scrollbar should appear
            height: t && b ? `${Math.floor(this.props.bottom - this.props.top - EDGE_WIDTH - BORDER_WIDTH - (this.props.arrowSide ? ARROW_SIZE : 0))}px` : 'auto',
            transform: l && t ? 'none' : (
                (l ? '' : !r ? 'translateX(-50%)' : 'translateX(-100%)') + ' ' +
                (t ? '' : !b ? 'translateY(-50%)' : 'translateY(-' + EDGE_WIDTH + 'px) translateY(-100%)')
            )
        };

        const arrowStyle = {};
        let contentStyle = {};
        let cls = `${this.props.className}`;
        if (this.props.arrowSide) {
            cls += ' arrowed';
            let arrowOffset = '-50%',
                arrowSelfOffset = '',
                arrowSizeOffset = '10px',
                flipOffset = '0';
            if (typeof this.props.arrowOffset === 'number') {
                if (this.props.arrowOffset >= 0) {
                    arrowOffset = `-${Math.round(this.props.arrowOffset)}px`;
                    arrowSelfOffset = `${Math.round(this.props.arrowOffset)}px`;
                }
                else {
                    flipOffset = '-100%';
                    arrowOffset = `${-Math.round(this.props.arrowOffset)}px`;
                    arrowSelfOffset = `calc(100% - ${-Math.round(this.props.arrowOffset)}px)`;
                }
            }

            switch (this.props.arrowSide) {
                case ArrowSide.LEFT:
                    cls += ' left';
                    style.transform = `translate(${arrowSizeOffset}, ${arrowOffset}) translate(0, ${flipOffset})`;
                    arrowStyle.top = arrowSelfOffset;
                    break;
                case ArrowSide.TOP:
                    cls += ' top';
                    style.transform = `translate(${arrowOffset}, ${arrowSizeOffset}) translate(${flipOffset}, 0)`;
                    arrowStyle.left = arrowSelfOffset;
                    break;
                case ArrowSide.RIGHT:
                    cls += ' right';
                    style.transform = `translate(-${arrowSizeOffset}, ${arrowOffset}) translate(-100%, ${flipOffset})`;
                    arrowStyle.top = arrowSelfOffset;
                    break;
                case ArrowSide.BOTTOM:
                    cls += ' bottom';
                    style.transform = `translate(${arrowOffset}, -${arrowSizeOffset}) translate(${flipOffset}, -100%)`;
                    arrowStyle.left = arrowSelfOffset;

                    if (style.height === "auto") {
                        contentStyle.maxHeight = `calc(${style.top} - ${EDGE_WIDTH + ARROW_SIZE + BORDER_WIDTH + parseInt(common.commonVerticalPadding) * 2}px)`;
                    }

                    break;
            }

            contentStyle = Object.assign(contentStyle, arrowStyle);
            delete contentStyle.left;
        }

        style.msTransform = style.transform;
        contentStyle.msTransform = contentStyle.transform;
        arrowStyle.msTransform = arrowStyle.transform;

        if (style.height !== 'auto') {
            contentStyle.height = contentStyle.maxHeight = '100%';
        }

        ReactDOM.render(
            <PassContext {...this.context} fixed={this.props.fixed} parent={this}>
                <div className={`popup ${cls}`} style={style} onMouseOver={this._onMouseOver} onMouseOut={this._onMouseOut} onClick={this._onClick}>
                    <div className="arrow" style={arrowStyle} />
                    <div className="content" style={contentStyle}>
                        {typeof this.props.children === 'function' ? this.props.children() : this.props.children}
                    </div>
                    {
                        // TODO when scroller appears in the content button appears on top of scroller - do something
                        this._isCloseMode(CloseMode.CLOSE_BUTTON) ?
                            <button className="close-button inline fa fa-times" onClick={this._onCloseClick} /> :
                            null
                    }
                </div>
            </PassContext>,
            this.container);
    }

    componentWillUnmount() {
        documentListeningComponents.splice(documentListeningComponents.indexOf(this), 1);

        this._mutationObserver.disconnect();
        removeEvent(this.container.firstElementChild, 'touchmove', this._onScroll);
        removeEvent(this.container.firstElementChild, 'mousewheel', this._onScroll);
        removeEvent(this.container.firstElementChild, 'DOMMouseScroll', this._onScroll);
        removeEvent(document.body, 'keydown', this._onOwnDocumentKeyDown);
        removeEvent(document.body, 'touchmove', this._onDocumentScroll);
        removeEvent(document.body, 'mousewheel', this._onDocumentScroll);
        removeEvent(document.body, 'DOMMouseScroll', this._onDocumentScroll);

        ReactDOM.unmountComponentAtNode(this.container);

        if (document.body) {
            document.body.removeChild(this.container);
        }

        this.container = null;

        if (this._mountingTimeoutID) {
            clearTimeout(this._mountingTimeoutID);
        }

        if (this._dependenciesTimeoutID) {
            clearTimeout(this._dependenciesTimeoutID);
        }
    }

    _isCloseMode(value) {
        return this.props.closeMode === value || Array.isArray(this.props.closeMode) && this.props.closeMode.includes(value);
    }

    _onDOMChange = () => {
        if (window.chrome) {
            // override chromium(?) bug
            // translate() with percentage values can produce half-pixel offsets
            // which makes content look blurry
            // here we force translatable content to have odd width and height so 50% we use below always produce integer values
            this._mutationObserver.disconnect();
            const content = this.container.firstElementChild.firstElementChild.nextElementSibling;
            content.style.paddingRight = `${(content.offsetWidth - parseInt(content.style.paddingRight || 0)) % 2}px`;
            content.style.paddingBottom = `${(content.offsetHeight - parseInt(content.style.paddingBottom || 0)) % 2}px`;
            this._mutationObserver.observe(this.container, { attributes: true, childList: true, characterData: true, subtree: true });
        }
        if (this.props.onResize) {
            this.props.onResize(this);
        }
    };

    _onMouseOver = (event) => {
        if (this.props.onMouseOver && isOverEvent(event, this.container.firstElementChild)) {
            this.props.onMouseOver(this, event);
        }
    };

    _onMouseOut = (event) => {
        if (this.props.onMouseOut && isOutEvent(event, this.container.firstElementChild)) {
            this.props.onMouseOut(this, event);
        }
    };

    _onScroll = (event) => {
        if (!event.isPostReactPropagationStopped) {
            let target = event.target;
            while (
                target !== document && target !== this.container.firstElementChild &&
                (target.scrollHeight <= target.clientHeight || window.getComputedStyle(target).overflowY !== 'auto' && window.getComputedStyle(target).overflowY !== 'scroll')) {
                target = target.parentNode;
            }
            if (
                (event.deltaY || event.wheelDeltaY || event.detail || 0) < 0 && target.scrollTop <= 0 ||
                (event.deltaY || event.wheelDeltaY || event.detail || 0) > 0 && target.scrollTop + target.clientHeight >= target.scrollHeight
            ) {
                event.preventDefault();
            }
            else {
                event.stopPropagation();
            }
        }
    };

    _onDocumentScroll = (event) => {
        if (this.props.dimmed && !event.isPostReactPropagationStopped) {
            event.preventDefault();
        }
    };

    _onDocumentKeyDown = (event) => {
        if (event.isPostReactPropagationStopped) {
            return;
        }
        if (event.keyCode === ESC_KEY_CODE) {
            if (this.props.closeMode && (!Array.isArray(this.props.closeMode) || this.props.closeMode.length)) {
                this._onCloseClick(event);
            }

            if (this.context.parent) {
                event.stopPropagation();
            }
        }
    };

    _onOwnDocumentKeyDown = (event) => {
        // modal kind of popups should block background document scrolling including wheel, arrow buttons, page up/down etc
        // but it shouldn't prevent other default actions bound to these events
        // TODO check why document.onscroll did not work correctly
        // OR
        // build a full list of conditions when default action is not document scroll
        if (
            [37, 38, 39, 40, 32, 33, 34, 35, 36].includes(event.keyCode) &&
            event.target.getAttribute('contentEditable') !== 'true' &&
            event.target.tagName !== 'BUTTON' &&
            event.target.tagName !== 'INPUT'
        ) {
            this._onDocumentScroll(event);
        }
    };

    _onCloseClick = (event) => {
        if (this.props.onClose && this.container) {
            this.props.onClose(this, event, CloseMode.CLOSE_BUTTON);
        }
    };

    _onClick = (event) => {
        if (event.isPostReactPropagationStopped) {
            return;
        }

        if (this._isCloseMode(CloseMode.INSIDE) && this.props.onClose && this.container) {
            this.props.onClose(this, event, CloseMode.INSIDE);
        }

        if (this.context.parent) {
            // each popup is placed in its own node in body and when some popups are nested, the click event from deepest one does not propagate through all parent popups
            // but goes directly to body and then to parent popups as an outside click, closing the parent popup if it's closing mode is "outside"
            // nested popups should be considered a part of parent popup's content
            // calling parent's click handler manually should help
            this.context.parent._onClick(event);
        }

        // the target element could be removed from DOM by it's own click handler, in this case by the time event bubbles to document it won't be inside Popup anymore
        // so we have to remember it as "clicked inside" for _onDocumentClick handler
        this._lastInnerClickTarget = event.target;
    };

    _onDocumentMouseDown = (nativeEvent) => {
        this._mouseStartPosition = {
            x: nativeEvent.x,
            y: nativeEvent.y
        };
    };

    _onDocumentClick = (nativeEvent) => {
        const
            xDifference = Math.abs(nativeEvent.x - this._mouseStartPosition.x),
            yDifference = Math.abs(nativeEvent.y - this._mouseStartPosition.y);

        if (!nativeEvent.isPostReactPropagationStopped &&
            this._isCloseMode(CloseMode.OUTSIDE) && this.container &&
            this._lastInnerClickTarget !== nativeEvent.target &&
            !isAncestorOrSelf(this.container.firstElementChild, nativeEvent.target) &&
            xDifference + yDifference <= 20) {
            if (this.props.onClose) {
                this.props.onClose(this, nativeEvent, CloseMode.OUTSIDE);
            }
        }

        this._lastInnerClickTarget = null;
    };

    render() {
        return null;
    }
}
